import {
    ACTIVITY_1011_CHECKIN_DAYS,
    ACTIVITY_1011_METRIC_KEYS,
    ACTIVITY_1011_SHARE_PLATFORMS,
    ACTIVITY_1011_TASK_TYPE_LABELS,
} from '@/constants/activity-1011';

import type {
    Activity1011Config,
    Activity1011ConfigSection,
    Activity1011MetricRule,
    Activity1011RewardRankTier,
    Activity1011ShareMetricsPointsRules,
    Activity1011TaskItem,
} from '@/types/activity-1011';

export const defaultCheckinDailyPoints = (): number[] =>
    Array.from({ length: ACTIVITY_1011_CHECKIN_DAYS }, () => 0);

export const createEmptyConfig = (): Activity1011Config => ({
    title: '',
    status: '1',
    activityId: 1011,
    activityStartAt: undefined,
    activityEndAt: undefined,
    tasks: [],
    checkinDailyPoints: defaultCheckinDailyPoints(),
    rewardRankTiers: [],
    sharePlatforms: [...ACTIVITY_1011_SHARE_PLATFORMS],
    shareMetricsPointsRules: {},
});

export const createTaskItem = (partial?: Partial<Activity1011TaskItem>): Activity1011TaskItem => ({
    taskId: partial?.taskId ?? '',
    type: partial?.type || '',
    description: partial?.description || '',
    points: partial?.points ?? 0,
    linkUrl: partial?.linkUrl || '',
});

export const createRewardRankTier = (
    partial?: Partial<Activity1011RewardRankTier>,
): Activity1011RewardRankTier => ({
    minRank: partial?.minRank ?? 1,
    maxRank: partial?.maxRank ?? 1,
    amount: partial?.amount ?? 0,
});

export const getTaskTypeLabel = (type: string) => ACTIVITY_1011_TASK_TYPE_LABELS[type] || type || '-';

const toNumber = (value: unknown, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const isBlankTaskId = (taskId: unknown) => {
    if (taskId === undefined || taskId === null || taskId === '') return true;
    const n = Number(taskId);
    return !Number.isFinite(n);
};

/** 取现有数字 taskId 的最大值 +1；没有数字时从 1 起。删除不回收。 */
export const getNextTaskId = (tasks: Activity1011TaskItem[] = []): number => {
    let max = 0;
    for (const task of tasks) {
        const n = Number(task.taskId);
        if (Number.isFinite(n) && n > max) max = n;
    }
    return max + 1;
};

/** 写出前兜底：空 / 非法 taskId 按顺序补齐，不改动已有有效 ID */
export const ensureTaskIds = (tasks: Activity1011TaskItem[] = []): Activity1011TaskItem[] => {
    let nextId = getNextTaskId(tasks);
    return tasks.map((task) => {
        if (!isBlankTaskId(task.taskId)) return task;
        const assigned = nextId;
        nextId += 1;
        return { ...task, taskId: assigned };
    });
};

const toOptionalMs = (value: unknown): number | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
};

const normalizeMetricRule = (raw: unknown): Activity1011MetricRule => {
    const obj = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    return {
        points: toNumber(obj.points, 0),
        threshold: toNumber(obj.threshold, 0),
    };
};

const normalizeCheckinPoints = (raw: unknown): number[] => {
    const base = defaultCheckinDailyPoints();
    if (!Array.isArray(raw)) return base;
    for (let i = 0; i < ACTIVITY_1011_CHECKIN_DAYS; i += 1) {
        base[i] = toNumber(raw[i], 0);
    }
    return base;
};

export const parseActivity1011ConfigValue = (raw: unknown): Activity1011Config => {
    const base = createEmptyConfig();
    if (!raw || typeof raw !== 'object') return base;

    const value = raw as Record<string, any>;
    const tasksRaw = Array.isArray(value.tasks) ? value.tasks : [];
    const platformsRaw = Array.isArray(value.sharePlatforms)
        ? value.sharePlatforms
        : Array.isArray(value.share_platforms)
          ? value.share_platforms
          : [...ACTIVITY_1011_SHARE_PLATFORMS];

    const rulesRaw = value.shareMetricsPointsRules || value.share_metrics_points_rules || {};
    const shareMetricsPointsRules: Activity1011ShareMetricsPointsRules = {};
    if (rulesRaw && typeof rulesRaw === 'object') {
        for (const [platform, metrics] of Object.entries(rulesRaw as Record<string, any>)) {
            if (!metrics || typeof metrics !== 'object') continue;
            shareMetricsPointsRules[platform] = {};
            for (const metricKey of Object.keys(metrics)) {
                shareMetricsPointsRules[platform]![metricKey] = normalizeMetricRule(metrics[metricKey]);
            }
        }
    }

    const tiersRaw = Array.isArray(value.rewardRankTiers)
        ? value.rewardRankTiers
        : Array.isArray(value.reward_rank_tiers)
          ? value.reward_rank_tiers
          : [];

    return {
        title: String(value.title ?? ''),
        status: String(value.status ?? '1'),
        activityId: value.activityId ?? value.activity_id ?? 1011,
        activityStartAt: toOptionalMs(value.activityStartAt ?? value.activity_start_at),
        activityEndAt: toOptionalMs(value.activityEndAt ?? value.activity_end_at),
        tasks: tasksRaw.map((item: Record<string, any>) =>
            createTaskItem({
                taskId: item.taskId ?? item.task_id ?? '',
                type: String(item.type ?? ''),
                description: String(item.description ?? item.name ?? ''),
                points: toNumber(item.points, 0),
                linkUrl: String(item.linkUrl ?? item.link_url ?? item.link ?? ''),
            }),
        ),
        checkinDailyPoints: normalizeCheckinPoints(
            value.checkinDailyPoints ?? value.checkin_daily_points,
        ),
        rewardRankTiers: tiersRaw.map((item: Record<string, any>) =>
            createRewardRankTier({
                minRank: toNumber(item.minRank ?? item.min_rank, 1),
                maxRank: toNumber(item.maxRank ?? item.max_rank, 1),
                amount: toNumber(item.amount, 0),
            }),
        ),
        sharePlatforms: platformsRaw.map((p: unknown) => String(p)),
        shareMetricsPointsRules,
    };
};

/** 写出真实 API value（只含服务端字段） */
export const buildActivity1011ConfigValue = (config: Activity1011Config): Record<string, unknown> => {
    const shareMetricsPointsRules: Activity1011ShareMetricsPointsRules = {};
    for (const [platform, metrics] of Object.entries(config.shareMetricsPointsRules || {})) {
        if (!metrics) continue;
        shareMetricsPointsRules[platform] = {};
        for (const [metricKey, rule] of Object.entries(metrics)) {
            if (!rule) continue;
            shareMetricsPointsRules[platform]![metricKey] = {
                points: toNumber(rule.points, 0),
                threshold: toNumber(rule.threshold, 0),
            };
        }
    }

    return {
        title: config.title?.trim() || '',
        status: String(config.status ?? '1'),
        activityId: toNumber(config.activityId, 1011),
        activityStartAt: config.activityStartAt,
        activityEndAt: config.activityEndAt,
        tasks: ensureTaskIds(config.tasks || []).map((task) => {
            const taskIdNum = toNumber(task.taskId, NaN);
            return {
                taskId: Number.isFinite(taskIdNum) ? taskIdNum : task.taskId,
                type: String(task.type || '').trim(),
                description: String(task.description || '').trim(),
                points: toNumber(task.points, 0),
                linkUrl: String(task.linkUrl || '').trim(),
            };
        }),
        checkinDailyPoints: normalizeCheckinPoints(config.checkinDailyPoints),
        rewardRankTiers: (config.rewardRankTiers || []).map((tier) => ({
            minRank: toNumber(tier.minRank, 1),
            maxRank: toNumber(tier.maxRank, 1),
            amount: toNumber(tier.amount, 0),
        })),
        sharePlatforms: (config.sharePlatforms || []).map((p) => String(p)),
        shareMetricsPointsRules,
    };
};

export const mergeSectionIntoConfig = (
    base: Activity1011Config,
    section: Activity1011ConfigSection,
    patch: Activity1011Config,
): Activity1011Config => {
    switch (section) {
        case 'basic':
            return {
                ...base,
                title: patch.title,
                status: patch.status,
                activityId: patch.activityId,
            };
        case 'time':
            return {
                ...base,
                activityStartAt: patch.activityStartAt,
                activityEndAt: patch.activityEndAt,
            };
        case 'tasks':
            return {
                ...base,
                tasks: patch.tasks,
            };
        case 'checkin':
            return {
                ...base,
                checkinDailyPoints: patch.checkinDailyPoints,
            };
        case 'spread':
            return {
                ...base,
                sharePlatforms: patch.sharePlatforms,
                shareMetricsPointsRules: patch.shareMetricsPointsRules,
            };
        case 'rewards':
            return {
                ...base,
                rewardRankTiers: patch.rewardRankTiers,
            };
        default:
            return base;
    }
};

export const ensurePlatformMetrics = (
    rules: Activity1011ShareMetricsPointsRules,
    platforms: string[],
): Activity1011ShareMetricsPointsRules => {
    const next: Activity1011ShareMetricsPointsRules = { ...rules };
    for (const platform of platforms) {
        next[platform] = { ...(next[platform] || {}) };
        for (const metric of ACTIVITY_1011_METRIC_KEYS) {
            if (!next[platform]![metric]) {
                next[platform]![metric] = { points: 0, threshold: 0 };
            }
        }
    }
    return next;
};
